import React from 'react';
import { Account } from '../types/Account';
import { formatMoney } from '../utils/format';
import { formatLastUpdated, isRemoved, isStale } from '../utils/accountStatus';
import './AccountCard.css';

export type AccountAction = 'remove' | 'restore' | 'convert' | 'updateBalance';

interface AccountCardProps {
  account: Account;
  onAction: (account: Account, action: AccountAction) => void;
  updating?: boolean;
}

const ACTION_LABELS: Record<AccountAction, string> = {
  remove: 'Mark removed',
  restore: 'Restore',
  convert: 'Make manual',
  updateBalance: 'Update',
};

// Manual rows already say "Manual", so their buttons can be terse.
const actionLabel = (account: Account, action: AccountAction): string =>
  account.isManual && action === 'remove' ? 'Remove' : ACTION_LABELS[action];

// Synced accounts that are fine need no actions; the rest get what applies.
const actionsFor = (
  account: Account,
  removed: boolean,
  stale: boolean,
): AccountAction[] => {
  if (removed) return ['restore'];
  if (account.isManual) return ['updateBalance', 'remove'];
  if (stale) return ['convert', 'remove'];
  return [];
};

const AccountCard: React.FC<AccountCardProps> = ({
  account,
  onAction,
  updating = false,
}) => {
  const { connection, balance } = account;
  const removed = isRemoved(account);
  const stale = !removed && isStale(account);
  const actions = actionsFor(account, removed, stale);
  // Brand-new manual accounts have no bank connection, so just show the type.
  const meta = [connection.name, account.type].filter(Boolean).join(' · ');

  const className = [
    'account-card',
    stale ? 'account-card-stale' : '',
    removed ? 'account-card-removed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <li className={className}>
      {connection.logo ? (
        <img
          src={connection.logo}
          alt={`${connection.name} logo`}
          className="account-logo"
        />
      ) : (
        <span className="account-logo account-logo-fallback" aria-hidden="true">
          {(connection.name || account.name).slice(0, 3).toUpperCase()}
        </span>
      )}
      <div className="account-detail">
        <div className="account-name">{account.name}</div>
        <div className="account-meta">{meta}</div>
        {stale && (
          <div className="account-status account-status-stale">
            {formatLastUpdated(balance.recordDate)}
          </div>
        )}
        {account.isManual && !removed && (
          <div className="account-status">
            Manual · {formatLastUpdated(balance.recordDate)}
          </div>
        )}
        {removed && (
          <div className="account-status">Removed · excluded from totals</div>
        )}
      </div>
      <div className="account-side">
        <div className="account-balance num">
          <span className="account-currency">{balance.currency}</span>
          {formatMoney(Number(balance.current)).slice(1)}
        </div>
        {actions.length > 0 && (
          <div className="account-actions">
            {actions.map((action) => (
              <button
                key={action}
                className="account-action"
                onClick={() => onAction(account, action)}
                disabled={updating}
              >
                {actionLabel(account, action)}
              </button>
            ))}
          </div>
        )}
      </div>
    </li>
  );
};

export default AccountCard;
